// @ts-nocheck
import React, { useState, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Upload, ChevronDown, ChevronRight, AlertTriangle, CheckCircle2, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { parseRLHoopsCsv, ParsedEvent, ParsedAgeGroup } from '@/services/rlhoopsCsvParser';
import { getScoringConfigs, calculatePoints } from '@/services/rlhoopsService';
import { getSessionUser } from '@/lib/authSession';

const EVENT_TYPES = [
  { value: 'non_live', label: 'Non-Live' },
  { value: 'may_live', label: 'May Live Period' },
];

const DIVISION_TIERS = [
  { value: 'premiere', label: 'Premiere' },
  { value: 'showcase', label: 'Showcase' },
  { value: 'elite', label: 'Elite' },
  { value: 'select', label: 'Select' },
];

function getPoolTier(poolLetter: string, category: 'high_school' | 'youth'): string {
  const code = poolLetter.charCodeAt(0) - 'A'.charCodeAt(0);
  if (category === 'high_school') {
    if (code <= 2) return 'A-C';
    if (code <= 5) return 'D-F';
    return 'G+';
  }
  // youth
  if (code <= 1) return 'A-B';
  if (code <= 3) return 'C-D';
  return 'E+';
}

// Deterministic UUID from team name + age group
function deterministicTeamId(teamName: string, ageGroup: string): string {
  const input = `${teamName.toLowerCase().trim()}|${ageGroup.toLowerCase().trim()}`;
  // Simple hash-based UUID v4 format (deterministic)
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    const chr = input.charCodeAt(i);
    hash = ((hash << 5) - hash) + chr;
    hash |= 0;
  }
  // Generate a second hash for more bits
  let hash2 = 0;
  for (let i = input.length - 1; i >= 0; i--) {
    const chr = input.charCodeAt(i);
    hash2 = ((hash2 << 7) - hash2) + chr;
    hash2 |= 0;
  }
  const hex1 = Math.abs(hash).toString(16).padStart(8, '0');
  const hex2 = Math.abs(hash2).toString(16).padStart(8, '0');
  const hex3 = Math.abs(hash ^ hash2).toString(16).padStart(8, '0');
  const hex4 = Math.abs(hash + hash2).toString(16).padStart(8, '0');
  return `${hex1.slice(0, 8)}-${hex2.slice(0, 4)}-4${hex3.slice(0, 3)}-a${hex4.slice(0, 3)}-${(hex1 + hex2).slice(0, 12)}`;
}

interface AgeGroupSelection {
  selected: boolean;
  expanded: boolean;
}

export default function RLHoopsCsvImport() {
  const [parsedEvent, setParsedEvent] = useState<ParsedEvent | null>(null);
  const [fileName, setFileName] = useState('');
  const [eventType, setEventType] = useState('');
  const [divisionTier, setDivisionTier] = useState('');
  const [ageGroupSelections, setAgeGroupSelections] = useState<Record<string, AgeGroupSelection>>({});
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState<{ games: number; entries: number; ageGroups: number } | null>(null);
  const [parseError, setParseError] = useState('');

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParseError('');
    setImportResult(null);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const content = ev.target?.result as string;
        const parsed = parseRLHoopsCsv(content);
        if (!parsed.ageGroups.length) {
          setParseError('No age groups found in CSV. Check the file format.');
          setParsedEvent(null);
          return;
        }
        setParsedEvent(parsed);
        const selections: Record<string, AgeGroupSelection> = {};
        parsed.ageGroups.forEach(ag => {
          selections[ag.ageGroup] = { selected: true, expanded: false };
        });
        setAgeGroupSelections(selections);
      } catch (err: any) {
        setParseError(err.message || 'Failed to parse CSV');
        setParsedEvent(null);
      }
    };
    reader.readAsText(file);
  }, []);

  const toggleAgeGroup = (ag: string, field: keyof AgeGroupSelection) => {
    setAgeGroupSelections(prev => ({
      ...prev,
      [ag]: { ...prev[ag], [field]: !prev[ag]?.[field] }
    }));
  };

  const selectedAgeGroups = useMemo(() => {
    if (!parsedEvent) return [];
    return parsedEvent.ageGroups.filter(ag => ageGroupSelections[ag.ageGroup]?.selected);
  }, [parsedEvent, ageGroupSelections]);

  const totalGamesToImport = useMemo(() => {
    return selectedAgeGroups.reduce((sum, ag) => sum + ag.games.length, 0);
  }, [selectedAgeGroups]);

  const handleImport = async () => {
    if (!parsedEvent || !eventType) {
      toast.error('Please select an event type');
      return;
    }
    if (eventType === 'may_live' && !divisionTier) {
      toast.error('Please select a division tier');
      return;
    }
    if (totalGamesToImport === 0) {
      toast.error('No games selected for import');
      return;
    }

    setImporting(true);
    setImportProgress(0);
    setImportResult(null);

    const { data: { user } } = await getSessionUser();

    // Pre-load existing team logos so we can preserve them on upsert
    const { data: existingLogos } = await supabase
      .from('rlhoops_team_rankings')
      .select('team_name, logo_url')
      .not('logo_url', 'is', null);
    const logoMap: Record<string, string> = {};
    (existingLogos || []).forEach((t: any) => {
      logoMap[t.team_name.toLowerCase()] = t.logo_url;
    });

    let totalEntries = 0;
    let totalGames = 0;
    let skippedGames = 0;
    const totalWork = totalGamesToImport;
    let processed = 0;

    try {
      for (const ag of selectedAgeGroups) {
        // Load scoring configs for this age group
        const configs = await getScoringConfigs({
          event_type: eventType,
          age_group: ag.ageGroup,
          category: ag.category,
        });

        for (const game of ag.games) {
          processed++;
          setImportProgress(Math.round((processed / totalWork) * 100));

          // Determine pool tier for each team
          const team1PoolTier = eventType === 'non_live'
            ? getPoolTier(game.team1Pool, ag.category as 'high_school' | 'youth')
            : undefined;
          const team2PoolTier = eventType === 'non_live'
            ? getPoolTier(game.team2Pool, ag.category as 'high_school' | 'youth')
            : undefined;

          // Find matching config for winner
          const findConfig = (poolTier?: string) => {
            if (eventType === 'may_live') {
              return configs.find(c => c.division_tier === divisionTier);
            }
            return configs.find(c => c.pool_tier === poolTier);
          };

          const winnerPool = game.winnerName === game.team1Name ? team1PoolTier : team2PoolTier;
          const loserPool = game.winnerName === game.team1Name ? team2PoolTier : team1PoolTier;

          const winnerConfig = findConfig(winnerPool);
          const loserConfig = findConfig(loserPool);

          if (!winnerConfig && !loserConfig) {
            skippedGames++;
            continue;
          }

          const winnerId = deterministicTeamId(game.winnerName, ag.ageGroup);
          const loserId = deterministicTeamId(game.loserName, ag.ageGroup);

          // Insert winner entry
          if (winnerConfig) {
            const winPoints = calculatePoints(winnerConfig, 'win', game.pointDifferential, false);
            const { error: winErr } = await supabase
              .from('rlhoops_ranking_points')
              .insert({
                team_id: winnerId,
                team_name: game.winnerName,
                points_earned: winPoints,
                result: 'win',
                point_differential: game.pointDifferential,
                opponent_is_shoe_team: false,
                scoring_config_id: winnerConfig.id,
                age_group: ag.ageGroup,
                season: '2025-2026',
                is_manual_entry: false,
                entered_by: user?.id || null,
              });
            if (!winErr) totalEntries++;
          }

          // Insert loser entry
          if (loserConfig) {
            const lossPoints = calculatePoints(loserConfig, 'loss', game.pointDifferential, false);
            const { error: lossErr } = await supabase
              .from('rlhoops_ranking_points')
              .insert({
                team_id: loserId,
                team_name: game.loserName,
                points_earned: lossPoints,
                result: 'loss',
                point_differential: game.pointDifferential,
                opponent_is_shoe_team: false,
                scoring_config_id: loserConfig.id,
                age_group: ag.ageGroup,
                season: '2025-2026',
                is_manual_entry: false,
                entered_by: user?.id || null,
              });
            if (!lossErr) totalEntries++;
          }

          totalGames++;
        }

        // Recalculate rankings for all teams in this age group
        const teamNames = new Set<string>();
        ag.games.forEach(g => { teamNames.add(g.winnerName); teamNames.add(g.loserName); });

        for (const teamName of teamNames) {
          const teamId = deterministicTeamId(teamName, ag.ageGroup);
          // Trigger recalculation by fetching totals
          const { data: entries } = await supabase
            .from('rlhoops_ranking_points')
            .select('points_earned, result')
            .eq('team_id', teamId)
            .eq('age_group', ag.ageGroup)
            .eq('season', '2025-2026');

          const totalPoints = (entries || []).reduce((sum, e) => sum + Number(e.points_earned), 0);
          const totalWins = (entries || []).filter(e => e.result === 'win').length;
          const totalLosses = (entries || []).filter(e => e.result === 'loss').length;
          const gamesPlayed = (entries || []).length;

          await supabase
            .from('rlhoops_team_rankings')
            .upsert({
              team_id: teamId,
              team_name: teamName,
              season: '2025-2026',
              age_group: ag.ageGroup,
              category: ag.category,
              total_points: totalPoints,
              total_wins: totalWins,
              total_losses: totalLosses,
              games_played: gamesPlayed,
              last_updated: new Date().toISOString(),
              // Preserve existing logo if one was previously set
              ...(logoMap[teamName.toLowerCase()] ? { logo_url: logoMap[teamName.toLowerCase()] } : {}),
            }, {
              onConflict: 'team_id,season,age_group'
            });
        }
      }

      setImportResult({
        games: totalGames,
        entries: totalEntries,
        ageGroups: selectedAgeGroups.length,
      });

      if (skippedGames > 0) {
        toast.warning(`Import complete. ${skippedGames} games skipped (no matching scoring config).`);
      } else {
        toast.success(`Imported ${totalGames} games (${totalEntries} point entries) across ${selectedAgeGroups.length} age groups`);
      }
    } catch (err: any) {
      toast.error(err.message || 'Import failed');
    } finally {
      setImporting(false);
      setImportProgress(100);
    }
  };

  const getPoolTierLabel = (pool: string, category: string) => {
    return getPoolTier(pool, category as 'high_school' | 'youth');
  };

  return (
    <div className="space-y-6">
      {/* Section 1: Upload & Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            CSV Import
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* File Upload */}
            <div className="space-y-2">
              <Label>Tournament Results CSV</Label>
              <div className="flex items-center gap-2">
                <label className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2 px-3 py-2 border border-dashed rounded-md hover:bg-accent/50 transition-colors">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground truncate">
                      {fileName || 'Choose .csv file...'}
                    </span>
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                </label>
              </div>
            </div>

            {/* Event Type */}
            <div className="space-y-2">
              <Label>Event Type</Label>
              <Select value={eventType} onValueChange={setEventType}>
                <SelectTrigger><SelectValue placeholder="Select event type" /></SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map(et => (
                    <SelectItem key={et.value} value={et.value}>{et.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Division Tier (only for may_live) */}
            {eventType === 'may_live' && (
              <div className="space-y-2">
                <Label>Division Tier</Label>
                <Select value={divisionTier} onValueChange={setDivisionTier}>
                  <SelectTrigger><SelectValue placeholder="Select division" /></SelectTrigger>
                  <SelectContent>
                    {DIVISION_TIERS.map(dt => (
                      <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {parseError && (
            <div className="flex items-center gap-2 text-destructive text-sm p-3 bg-destructive/10 rounded-md">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              {parseError}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 2: Parsed Preview */}
      {parsedEvent && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Parsed Preview</CardTitle>
            <div className="text-sm text-muted-foreground">
              <span className="font-medium">{parsedEvent.eventName}</span> — {parsedEvent.eventDate}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {parsedEvent.ageGroups.map(ag => {
              const sel = ageGroupSelections[ag.ageGroup];
              const blowouts = ag.games.filter(g => g.isBlowout).length;
              const poolLetters = [...new Set(ag.pools.map(p => p.letter))];

              return (
                <Collapsible
                  key={ag.ageGroup}
                  open={sel?.expanded}
                  onOpenChange={() => toggleAgeGroup(ag.ageGroup, 'expanded')}
                >
                  <div className="flex items-center gap-3 p-3 border rounded-md">
                    <Checkbox
                      checked={sel?.selected}
                      onCheckedChange={() => toggleAgeGroup(ag.ageGroup, 'selected')}
                    />
                    <CollapsibleTrigger className="flex items-center gap-2 flex-1 text-left">
                      {sel?.expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      <span className="font-semibold">{ag.ageGroup.toUpperCase()}</span>
                      <span className="text-sm text-muted-foreground">
                        — {ag.games.length} games across {ag.pools.length} pools
                      </span>
                      <Badge variant="outline" className="ml-auto text-xs">
                        {ag.category === 'high_school' ? 'HS' : 'Youth'}
                      </Badge>
                    </CollapsibleTrigger>
                  </div>

                  <CollapsibleContent className="pl-10 pr-3 py-2 space-y-2">
                    {/* Pool breakdown */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {ag.pools.map(pool => (
                        <div key={pool.letter} className="text-sm p-2 bg-muted/50 rounded">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">Pool {pool.letter}</span>
                            <Badge variant="secondary" className="text-xs">
                              → {getPoolTierLabel(pool.letter, ag.category)}
                            </Badge>
                          </div>
                          <span className="text-muted-foreground">{pool.teams.length} teams</span>
                        </div>
                      ))}
                    </div>

                    {/* Game stats */}
                    <div className="flex gap-4 text-sm">
                      <span>{ag.games.length} total games</span>
                      {blowouts > 0 && (
                        <span className="text-destructive flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          {blowouts} blowout{blowouts !== 1 ? 's' : ''} (≥20 pts)
                        </span>
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}

            {/* Total */}
            <div className="pt-3 border-t flex items-center justify-between">
              <span className="font-medium">Total games to import: {totalGamesToImport}</span>
              <span className="text-sm text-muted-foreground">
                ({totalGamesToImport * 2} point entries)
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section 3: Pool Tier Mapping Reference */}
      {parsedEvent && eventType === 'non_live' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Pool → Tier Mapping</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-sm mb-2">High School (15U–17U)</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between p-1.5 bg-muted/30 rounded">
                    <span>Pools A, B, C</span><Badge variant="outline">A-C</Badge>
                  </div>
                  <div className="flex justify-between p-1.5 bg-muted/30 rounded">
                    <span>Pools D, E, F</span><Badge variant="outline">D-F</Badge>
                  </div>
                  <div className="flex justify-between p-1.5 bg-muted/30 rounded">
                    <span>Pools G+</span><Badge variant="outline">G+</Badge>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-sm mb-2">Youth (11U–14U)</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between p-1.5 bg-muted/30 rounded">
                    <span>Pools A, B</span><Badge variant="outline">A-B</Badge>
                  </div>
                  <div className="flex justify-between p-1.5 bg-muted/30 rounded">
                    <span>Pools C, D</span><Badge variant="outline">C-D</Badge>
                  </div>
                  <div className="flex justify-between p-1.5 bg-muted/30 rounded">
                    <span>Pools E+</span><Badge variant="outline">E+</Badge>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section 4: Import */}
      {parsedEvent && (
        <Card>
          <CardContent className="py-4">
            {importing && (
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span>Importing games...</span>
                  <span>{importProgress}%</span>
                </div>
                <Progress value={importProgress} />
              </div>
            )}

            {importResult && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-accent/30 border border-accent rounded-md text-foreground">
                <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-primary" />
                <span>
                  Imported {importResult.games} games ({importResult.entries} point entries) across {importResult.ageGroups} age group{importResult.ageGroups !== 1 ? 's' : ''}
                </span>
              </div>
            )}

            <Button
              onClick={handleImport}
              disabled={importing || !parsedEvent || totalGamesToImport === 0 || !eventType}
              className="w-full"
              size="lg"
            >
              {importing ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Importing...</>
              ) : (
                <>Import {totalGamesToImport} Games ({totalGamesToImport * 2} entries)</>
              )}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
